/**
 * Filtering and counting items — the assistant's "database query", and useful
 * to any screen. Structured on purpose: it only ever sees the items the
 * caller's repository already returned, so it can't reach anything the person
 * couldn't (docs/ASSISTANT.md). No raw SQL, by design.
 */
import { z } from 'zod'
import { type Item, itemStatuses, statusLabels } from './item.ts'

export const itemQuerySchema = z.object({
  status: z.array(z.enum(itemStatuses)).optional().describe('Only these statuses'),
  text: z
    .string()
    .max(100)
    .optional()
    .describe('Words that must appear in the title or description'),
  createdAfter: z.iso.date().optional().describe('YYYY-MM-DD, inclusive'),
  createdBefore: z.iso.date().optional().describe('YYYY-MM-DD, exclusive'),
  createdBy: z.string().max(100).optional().describe("Part of the creator's name"),
  includeDeleted: z.boolean().optional().describe('Also count items in Recently deleted'),
  groupBy: z
    .enum(['status', 'createdBy', 'createdMonth'])
    .optional()
    .describe('Count the matches per status, per creator, or per month'),
  limit: z
    .number()
    .int()
    .min(0)
    .max(50)
    .optional()
    .describe('How many matching items to list (default 20)'),
})
export type ItemQuery = z.infer<typeof itemQuerySchema>

export const itemQueryResultSchema = z.object({
  total: z.number(),
  /** True when more records exist than were scanned, so counts are a floor. */
  scanLimited: z.boolean().default(false),
  groups: z.array(z.object({ key: z.string(), label: z.string(), count: z.number() })),
  items: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      status: z.enum(itemStatuses),
      createdAt: z.string(),
      createdBy: z.string().nullable(),
      deleted: z.boolean(),
    }),
  ),
})
export type ItemQueryResult = z.infer<typeof itemQueryResultSchema>

/** The filters a query and an export share — everything but grouping and how many to list. */
export type ItemFilter = Pick<
  ItemQuery,
  'status' | 'text' | 'createdAfter' | 'createdBefore' | 'createdBy'
>

/** Whether one item passes the filters — the one rule `queryItems` and the items export follow. */
export function itemMatches(item: Item, filter: ItemFilter): boolean {
  if (filter.status && !filter.status.includes(item.status)) return false
  const day = item.createdAt.slice(0, 10)
  if (filter.createdAfter && day < filter.createdAfter) return false
  if (filter.createdBefore && day >= filter.createdBefore) return false
  const creator = filter.createdBy?.toLowerCase()
  if (creator && !(item.createdBy?.name.toLowerCase().includes(creator) ?? false)) return false
  const words = (filter.text ?? '').toLowerCase().split(/\s+/).filter(Boolean)
  const haystack = `${item.title} ${item.description}`.toLowerCase()
  return words.every((word) => haystack.includes(word))
}

/** Every field an items export can hold, in the order a spreadsheet shows them. */
export const itemExportFields = [
  'id',
  'title',
  'description',
  'status',
  'createdAt',
  'createdBy',
] as const
export type ItemExportField = (typeof itemExportFields)[number]

/**
 * MCP's items_export (docs/MCP.md "Export links"): the same filters as
 * `itemQuerySchema`, which fields, and which file format. Active items only;
 * Recently deleted is its own list.
 */
export const itemExportInputSchema = itemQuerySchema
  .pick({ status: true, text: true, createdAfter: true, createdBefore: true, createdBy: true })
  .extend({
    fields: z
      .array(z.enum(itemExportFields))
      .min(1)
      .optional()
      .describe('Which columns, in this order (all of them when left out)'),
    format: z.enum(['csv', 'ndjson']).default('csv').describe('csv (default) or ndjson'),
  })
export type ItemExportInput = z.infer<typeof itemExportInputSchema>

/** One item as an export row: `createdBy` as the person's name, a plain value per column. */
export function itemExportRow(item: Item): Record<ItemExportField, string | null> {
  return {
    id: item.id,
    title: item.title,
    description: item.description,
    status: item.status,
    createdAt: item.createdAt,
    createdBy: item.createdBy?.name ?? null,
  }
}

export function queryItems(
  active: readonly Item[],
  deleted: readonly Item[],
  query: ItemQuery,
): ItemQueryResult {
  const pool = [
    ...active.map((item) => ({ item, deleted: false })),
    ...(query.includeDeleted ? deleted.map((item) => ({ item, deleted: true })) : []),
  ]
  const matches = pool.filter(({ item }) => itemMatches(item, query))

  const groups = new Map<string, { label: string; count: number }>()
  if (query.groupBy) {
    for (const { item } of matches) {
      const [key, label] =
        query.groupBy === 'status'
          ? [item.status, statusLabels[item.status]]
          : query.groupBy === 'createdBy'
            ? [item.createdBy?.id ?? 'unknown', item.createdBy?.name ?? 'Unknown']
            : [item.createdAt.slice(0, 7), item.createdAt.slice(0, 7)]
      const entry = groups.get(key) ?? { label, count: 0 }
      entry.count += 1
      groups.set(key, entry)
    }
  }

  return {
    // The caller says whether more records existed than were scanned.
    scanLimited: false,
    total: matches.length,
    groups: [...groups.entries()]
      .map(([key, { label, count }]) => ({ key, label, count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
    items: matches
      .sort((a, b) => b.item.createdAt.localeCompare(a.item.createdAt))
      .slice(0, query.limit ?? 20)
      .map(({ item, deleted: isDeleted }) => ({
        id: item.id,
        title: item.title,
        status: item.status,
        createdAt: item.createdAt,
        createdBy: item.createdBy?.name ?? null,
        deleted: isDeleted,
      })),
  }
}
